import { withRouter } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { Button, Card, CardBody, Col, Row, CardHeader, Spinner, Input } from "reactstrap";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { EMPTY_LIST, INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, {
    PaginationProvider,
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';
import { 
    ComplianceQueryChoices,
    ComplianceQueryItemTypes
} from "src/modules/3rd-party-management/constants";
import DateUtils from "src/services/utils/DateUtils";
import FilteringComponent from "./components/FilteringComponent";
import OverviewHeader from "./components/OverviewHeader";
import QueryContext from "../../../../contexts/QueryContext";

import ComplianceScreeningQueriesService from "src/modules/3rd-party-management/apis/admin/compliance/screening/QueriesService";
import ComplianceScreeningQueryItemService from "src/modules/3rd-party-management/apis/admin/compliance/screening/QueryItemService";

import ComplianceMonitoringQueriesService from "src/modules/3rd-party-management/apis/admin/compliance/monitoring/QueriesService";
import ComplianceMonitoringQueryItemService from "src/modules/3rd-party-management/apis/admin/compliance/monitoring/QueryItemService";

const SearchInputs = ({ t, queryData, queryType }) => {
    const dateUtils = new DateUtils();
    
    const wrapperRef = useRef(null);

    const [filters, setFilters] = useState({
        pageIndex: 1,
        pageSize: 25
    });

    const [ items, setItems ] = useState([]);

    const itemService = queryType === 'screening' ? 
        ComplianceScreeningQueryItemService.getInstance() : 
        ComplianceMonitoringQueryItemService.getInstance(); 

    const queriesService = queryType === 'screening' ? 
        ComplianceScreeningQueriesService.getInstance() : 
        ComplianceMonitoringQueriesService.getInstance();

    const handleToggleQueryItemMutation = useMutation({
        mutationFn: async ({ itemId }) => {
            return await itemService.toggleActiveStatus(itemId);
        },
        onSuccess: (_, { value, itemId }) => {
            setItems(prev => {
                const itemsCopy = [...prev];
                const itemIndex = itemsCopy.findIndex((item) => item.id === itemId);

                if (itemIndex !== -1) {
                    itemsCopy[itemIndex] = {
                        ...itemsCopy[itemIndex],
                        isEnabled: value
                    }
                }

                return itemsCopy;
            });

            if (value) {
                toast(t('Item added to list successfully.'), {
                    type: 'success'
                });
            }
            else {
                toast(t('Item removed from list successfully.'), {
                    type: 'success'
                });
            }
        },
        onError: (_, { value }) => {
            if (value) {
                toast(t('An error occurred while adding item to list.'), {
                    type: 'error'
                });
            }
            else {
                toast(t('An error occurred while removing item from list.'), {
                    type: 'error'
                });
            }
        },
        onSettled: (_, __, { itemId }) => {
            setItems(prev => {
                const itemsCopy = [...prev];
                const itemIndex = itemsCopy.findIndex((item) => item.id === itemId);

                if (itemIndex !== -1) {
                    itemsCopy[itemIndex] = {
                        ...itemsCopy[itemIndex],
                        isLoading: false
                    }
                }

                return itemsCopy;
            });
        }
    });

    const handleToggle = (itemId, value) => {
        setItems(prev => {
            const itemsCopy = [...prev];
            const itemIndex = itemsCopy.findIndex((item) => item.id === itemId);

            if (itemIndex !== -1) {
                itemsCopy[itemIndex] = {
                    ...itemsCopy[itemIndex],
                    isLoading: true
                }
            }

            return itemsCopy;
        });

        handleToggleQueryItemMutation.mutate(
            { itemId, value }
        );
    }

    const selectRow = {
        mode: 'checkbox',
        clickToSelect: false,
        onSelect: (row) => {
            handleToggle(row.id, !row.isEnabled);
        },
        selectionRenderer: ({ rowKey }) => {
            const row = items?.find((item) => item.id === rowKey);

            if (row?.isLoading) {
                return (
                    <Spinner animation="border" variant="primary" size="sm" />
                )
            }

            return (
                <Input
                    type="checkbox"
                    className="mt-2"
                    checked={row?.isEnabled || false} 
                />
            )
        },
        hideSelectAll: true
    };

    const tableColumns = [
        {
            dataField: "secondaryId",
            text: t("Number"),
            sort: false
        },
        {
            dataField: "supplier",
            text: t("Search Item"),
            sort: false,
            style: {
                width: '200px'
            },
            formatter: (_cell, { choiceKey, itemType, ...elseProps }) => {
                switch (itemType) {
                    case 'organization_legal':
                        return elseProps.organizationLegal?.legalEntity?.title || '--';
                    case 'share_holder':
                        return (choiceKey === 'shareholder_company' ? elseProps.shareHolder?.companyName : elseProps.shareHolder?.fullName) ||  '--';
                    case 'kyb_management_member':
                        return elseProps.kybManagementTeamMember?.fullName || '--';
                    case 'team_manager':
                        return elseProps.teamManager?.fullName || '--';
                    case 'organization':
                        return elseProps.organization?.name || '--';
                    default:
                        return '--';
                }
            }
        },
        {
            dataField: "supplier.name",
            text: t("Third Party"),
            sort: false
        },
        {
            dataField: "type",
            text: t("Type"),
            sort: false,
            formatter: (cell) => {
                const queryType = ComplianceQueryItemTypes.find((type) => type.value === cell);

                return t(queryType?.label || cell) || '--';
            }
        },
        {
            dataField: "organization.registrationNumber",
            text: t("Registration Number"),
            sort: false,
            formatter: (cell) => {
                return cell || '--';
            }
        },
        {
            dataField: "itemType",
            text: t("Relation"),
            sort: false,
            formatter: (cell, { choiceKey }) => {
                const choice = ComplianceQueryChoices.find((choice) => choice.value === choiceKey);

                const relation = choice?.relations?.find((relation) => relation.value === cell);

                return `${t(relation?.label || cell)}` ;
            }
        },
        {
            dataField: "choiceKey",
            text: t("Date of Birth"),
            sort: false,
            formatter: (cell, { itemType, ...elseProps }) => {
                if (cell === 'shareholder_individuals') {
                    if (elseProps.shareHolder?.datesOfBirth) {
                        return dateUtils.convertDateToDate(
                            elseProps.shareHolder.datesOfBirth,
                            INTERNATIONAL_DATE_FORMAT
                        );
                    }
                }

                if (cell === 'ubo') {
                    if (elseProps.shareHolder?.datesOfBirth) {
                        return dateUtils.convertDateToDate(
                            elseProps.shareHolder.datesOfBirth,
                            INTERNATIONAL_DATE_FORMAT
                        );
                    }
                }

                if (cell === 'management') {
                    if (itemType === 'kyb_management_member') {
                        if (elseProps.kybManagementTeamMember?.dateOfBirth) {
                            return dateUtils.convertDateToDate(
                                elseProps.kybManagementTeamMember.dateOfBirth,
                                INTERNATIONAL_DATE_FORMAT
                            );
                        }
                    }

                    if (itemType === 'team_manager') {
                        if (elseProps.teamManager?.dateOfBirth) {
                            return dateUtils.convertDateToDate(
                                elseProps.teamManager.dateOfBirth,
                                INTERNATIONAL_DATE_FORMAT
                            );
                        }
                    }
                }

                return '--';
            }
        },
        {
            dataField: "fuzziness",
            text: t("Fuzziness"),
            sort: false,
            formatter: () => {
                return '80%'
            }
        }
    ];

    const {
        data: resultData,
        isFetching: itemsFetching,
        isFetched: itemsFetched
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-compliance-query-items',
            queryData.id,
            filters
        ],
        queryFn: async () => {
            return await queriesService.fetchQueryItems(queryData.id, filters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('Failed to load search items. Please try again.'), {
                type: 'error'
            });
        }
    });

    useEffect(() => {
        if (resultData?.items) {
            setItems(resultData.items);
        }
    }, [ resultData ])

    const handleFilterChange = useCallback((newFilters) => {
        setFilters((prev) => {
            return {
                ...prev,
                ...newFilters,
                pageIndex: 1
            }
        });
    }, []);

    const handleTableChange = (type, options) => {
        const {
            page,
            sizePerPage,

            sortField,
            sortOrder
        } = options;

        switch (type) {
            case 'pagination':
                setFilters({
                    ...filters,
                    pageIndex: page,
                    pageSize: sizePerPage
                });
                break;
            case 'sort':
                setFilters({
                    ...filters,
                    sortField: sortField,
                    sortOrder: sortOrder.toUpperCase()
                })
                break;
            default:
                return false;
        }
    }

    const NoDataIndication = () => (
        (itemsFetched && !(items?.follow_list_results || []).length) ? <div className="alert m-0" role="alert">
            <p style={{
                textAlign: "center",
                marginBottom: 0
            }}>
                {t(EMPTY_LIST)}
            </p>
        </div> : <></>
    );

    const totalItemsCount = parseInt(resultData?.itemsCount || 0);

    return (
        <div className="compliance-monitoring">
            <Row>
                <Col sm="12">
                    <div
                        style={{
                            position: 'sticky',
                            top: '74px',
                            zIndex: 10
                        }}
                        className="d-flex justify-content-end align-items-center mb-3"
                    >
                        <div className="d-flex justify-content-end align-items-center bg-white">
                            <div className="d-flex">
                                <FilteringComponent
                                    onChange={handleFilterChange}
                                />
                            </div>
                        </div>
                    </div>
                </Col>

                <Col sm="12">
                    <PaginationProvider
                        pagination={
                            paginationFactory({
                                custom: true,
                                page: filters.pageIndex,
                                sizePerPage: filters.pageSize,
                                totalSize: totalItemsCount,
                                withFirstAndLast: true,
                                alwaysShowAllBtns: true,
                                prePageText: <span><i className="ri-arrow-left-s-line"></i> {t('Back')}</span>,
                                nextPageText: <span>{t('Next')} <i className="ri-arrow-right-s-line"></i></span>,
                                prePageTitle: t('Pre page'),
                                firstPageTitle: t('First page'),
                                lastPageTitle: t('Last page'),
                                showTotal: false,
                                paginationTotalRenderer: (from, to, size) => {
                                    return (
                                        <span className="react-bootstrap-table-pagination-total">
                                            {`${t('Total Items')} ${size}`}
                                        </span>
                                    )
                                },
                                hideSizePerPage: false,
                                sizePerPageList: [
                                    {
                                        text: '25',
                                        value: 25
                                    },
                                    {
                                        text: '50',
                                        value: 50
                                    }
                                ]
                            })
                        }>
                        {
                            ({
                                paginationProps,
                                paginationTableProps
                            }) => (
                                <>
                                    <Row>
                                        <Col sm="12" className="mt-4">
                                            <div ref={wrapperRef}>
                                                <div className="table-responsive">
                                                    <BootstrapTable
                                                        remote={{
                                                            pagination: true,
                                                            filter: false,
                                                            sort: true,
                                                            cellEdit: false,
                                                            search: false
                                                        }}
                                                        loading={itemsFetching}
                                                        overlay={overlayFactory({ spinner: <Spinner animation="border" variant="primary" size="md" />, text: `${t("Loading")}...` })}
                                                        onTableChange={handleTableChange}
                                                        defaultSorted={[]}
                                                        keyField="id"
                                                        responsive={false}
                                                        bordered={false}
                                                        data={items}
                                                        striped={false}
                                                        columns={tableColumns}
                                                        classes={"table tpdd-table compliance-monitoring-table"}
                                                        headerWrapperClasses={"thead-light"}
                                                        style={{
                                                            overflowX: "auto",
                                                        }}
                                                        noDataIndication={() => <NoDataIndication />}
                                                        selectRow={selectRow}
                                                        {...paginationTableProps}
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm="12">
                                            <div className="tpdd-pagination-style-1">
                                                <PaginationTotalStandalone
                                                    {...paginationProps}
                                                />

                                                {totalItemsCount > filters.pageSize && (
                                                    <div>
                                                        <PaginationListStandalone
                                                            {...paginationProps}
                                                        />

                                                        <SizePerPageDropdownStandalone
                                                            {...paginationProps}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                </>
                            )
                        }

                    </PaginationProvider>
                </Col>
            </Row>
        </div>
    )
}

const SearchItems = ({
    t,
    onNextButtonClicked,
    onBackButtonClicked,
    onCancelButtonClicked
}) => {
    const {
        queryData,
        queryType
    } = useContext(QueryContext);

    return (
        <div>
            <div
                className="mb-4"
            >
                <OverviewHeader />
            </div>

            <Card>
                <CardHeader className="bg-white">
                    <h5 className="mb-1">
                        {t('Select Search Items')}
                    </h5>
                    <p className="text-muted mb-0">
                        {t(queryType === 'screening' ? 'Review and refine the entities to be screened by selecting or removing items from the list.' : 'Review and refine the entities to be monitored by selecting or removing items from the list.')}
                    </p>
                </CardHeader>

                <CardBody>
                    <Row>
                        <Col sm='12' className="mb-4">
                            <SearchInputs 
                                t={t}
                                queryData={queryData}
                                queryType={queryType}
                            />
                        </Col>
                        <Col sm='12' className="d-flex justify-content-between">
                            <Button
                                color="secondary"
                                outline
                                type="button"
                                onClick={onCancelButtonClicked}
                            >
                                {t('Cancel')}
                            </Button>

                            <div>
                                <Button
                                    color="secondary"
                                    onClick={onBackButtonClicked}
                                    className="me-2"
                                    type="button"
                                >
                                    {t('Back')}
                                </Button>

                                <Button
                                    color="primary"
                                    type="button"
                                    onClick={onNextButtonClicked}
                                >
                                    {t('Next')}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
        </div>
    )
}

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    return {
        user: state.Login.user,
        token: token
    };
};

export default withNamespaces()(
    withRouter(connect(
        mapStatetoProps,
        {}
    )(SearchItems))
)